import React, { useState, useEffect } from 'react';
import './DictionaryTable.css';
import { dictionaryEntryApi } from '../../../services/apis/configuredApis';

function DictionaryEntryTable({ onEdit, graphId }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [filterScope, setFilterScope] = useState('all');
  const [filterType, setFilterType] = useState('all');

  const fetchEntries = async (search = null) => {
    setLoading(true);
    setError(null);
    try {
      const response = await dictionaryEntryApi.dictionaryEntryListGet({
        search,
        limit: 500,
        offset: 0,
      });
      const responseData = response?.data || response;
      const entriesList = Array.isArray(responseData) ? responseData : (responseData?.entries || []);
      
      let filtered = entriesList;
      if (graphId) {
        filtered = entriesList.filter(e => e.graph_id === graphId || e.scope_type === 'GLOBAL');
      }
      
      setEntries(filtered);
    } catch (err) {
      console.error('Error fetching dictionary entries:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch dictionary entries');
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, [graphId]);

  useEffect(() => {
    const searchTimeout = setTimeout(() => {
      const term = searchTerm.toLowerCase();
      let filtered = entries.filter(e => {
        const matchesSearch = !searchTerm ||
          e.synonym?.toLowerCase().includes(term) ||
          e.synonym_norm?.toLowerCase().includes(term) ||
          e.canonical_term_id?.toLowerCase().includes(term);

        const matchesScope = filterScope === 'all' ||
          e.scope_type?.toUpperCase() === filterScope.toUpperCase();

        let matchesType = true;
        if (filterType === 'domain') {
          matchesType = e.canonical_term_id?.startsWith('mlcf:domain:');
        } else if (filterType === 'context') {
          matchesType = e.canonical_term_id?.startsWith('mlcf:context:');
        } else if (filterType === 'other') {
          matchesType = !e.canonical_term_id?.startsWith('mlcf:domain:') &&
                        !e.canonical_term_id?.startsWith('mlcf:context:');
        }

        return matchesSearch && matchesScope && matchesType;
      });
      setFilteredEntries(filtered);
    }, 300);

    return () => clearTimeout(searchTimeout);
  }, [searchTerm, filterScope, filterType, entries]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDelete = async (entry) => {
    if (window.confirm(`Are you sure you want to delete "${entry.synonym}"?`)) {
      try {
        await dictionaryEntryApi.dictionaryEntryDeleteEntryIdDelete(entry.id);
        // Refresh the list after deletion
        await fetchEntries(searchTerm || null);
      } catch (err) {
        console.error('Error deleting dictionary entry:', err);
        alert('Failed to delete dictionary entry. Please try again.');
      }
    }
  };

  if (loading && entries.length === 0) {
    return <div className="dictionary-loading">Loading dictionary entries...</div>;
  }

  if (error && entries.length === 0) {
    return (
      <div className="dictionary-table-container">
        <div className="dictionary-empty">
          <div className="dictionary-empty-icon">⚠️</div>
          <h3>Error Loading Dictionary Entries</h3>
          <p>{error}</p>
          <button
            onClick={() => fetchEntries()}
            style={{
              marginTop: '1rem',
              padding: '0.5rem 1rem',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dictionary-table-container">
      <div className="dictionary-table-controls">
        <input
          type="text"
          className="dictionary-search"
          placeholder="Search dictionary entries..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select
          className="dictionary-filter"
          value={filterScope}
          onChange={(e) => setFilterScope(e.target.value)}
        >
          <option value="all">All Scopes</option>
          <option value="GLOBAL">Global</option>
          <option value="GRAPH">Graph</option>
        </select>
        <select
          className="dictionary-filter"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
        >
          <option value="all">All Types</option>
          <option value="domain">Domain Vocabulary</option>
          <option value="context">Context Categories</option>
          <option value="other">Other (non-MLCF)</option>
        </select>
      </div>

      {filteredEntries.length === 0 ? (
        <div className="dictionary-empty">
          <div className="dictionary-empty-icon">📖</div>
          <h3>No Dictionary Entries Found</h3>
          <p>
            {searchTerm || filterScope !== 'all' || filterType !== 'all'
              ? 'No entries match your filters.'
              : 'Get started by creating your first dictionary entry.'}
          </p>
        </div>
      ) : (
        <div className="dictionary-table-wrapper">
          <table className="dictionary-table">
            <thead>
              <tr>
                <th>Synonym</th>
                <th>Normalized</th>
                <th>Canonical Term ID</th>
                <th>Scope</th>
                <th>Graph ID</th>
                <th>Pattern</th>
                <th>Override Global</th>
                <th>Enabled</th>
                <th>Weight</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredEntries.map((entry) => (
                <tr key={entry.id}>
                  <td>
                    <strong>{entry.synonym}</strong>
                  </td>
                  <td className="dictionary-meta">{entry.synonym_norm}</td>
                  <td className="dictionary-meta">
                    <span className="dictionary-badge dictionary-badge-info">
                      {entry.canonical_term_id?.substring(0, 10)}...
                    </span>
                  </td>
                  <td>
                    <span className={`dictionary-badge dictionary-badge-${entry.scope_type?.toLowerCase()}`}>
                      {entry.scope_type}
                    </span>
                  </td>
                  <td className="dictionary-meta">
                    {entry.graph_id ? (
                      <span className="dictionary-badge dictionary-badge-info">
                        {entry.graph_id.substring(0, 8)}...
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>
                    <span
                      className={`dictionary-status ${
                        entry.is_pattern
                          ? 'dictionary-status-active'
                          : 'dictionary-status-inactive'
                      }`}
                    >
                      {entry.is_pattern ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`dictionary-status ${
                        entry.override_global
                          ? 'dictionary-status-active'
                          : 'dictionary-status-inactive'
                      }`}
                    >
                      {entry.override_global ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`dictionary-status ${
                        entry.is_enabled
                          ? 'dictionary-status-active'
                          : 'dictionary-status-inactive'
                      }`}
                    >
                      {entry.is_enabled ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td className="dictionary-meta">{entry.weight || 0}</td>
                  <td className="dictionary-meta">{formatDate(entry.created_at)}</td>
                  <td>
                    <div className="dictionary-actions">
                      <button
                        className="dictionary-action-btn dictionary-action-btn-edit"
                        onClick={() => onEdit(entry)}
                        title="Edit Entry"
                      >
                        ✏️
                      </button>
                      <button
                        className="dictionary-action-btn dictionary-action-btn-delete"
                        onClick={() => handleDelete(entry)}
                        title="Delete Entry"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default DictionaryEntryTable;

